import client from "./client";
import { cacheDelete, TTL, userCacheKey } from "../lib/cache";
import { fetchCached, readCached } from "./fetchCached";

export const PROJECTS_KEY = (user) => userCacheKey(user, "projects");
export const PROJECT_LIBRARY_KEY = (user, projectId) =>
  userCacheKey(user, `project:${projectId}:library`);

export function invalidateProjects(user) {
  cacheDelete(PROJECTS_KEY(user));
}

export function invalidateProjectLibrary(user, projectId) {
  cacheDelete(PROJECT_LIBRARY_KEY(user, projectId));
}

/**
 * @param {object|null} user
 * @param {{ revalidate?: boolean }} [opts]
 */
export async function listProjects(user, { revalidate = false } = {}) {
  const key = PROJECTS_KEY(user);
  const { data } = await fetchCached({
    key,
    ttl: TTL.PROJECTS,
    revalidate,
    fetcher: async () => {
      const res = await client.get("/me/projects");
      return res.data;
    },
  });
  return data;
}

/** Synchronous read for initial render (may be null). */
export function getCachedProjects(user) {
  return readCached(PROJECTS_KEY(user));
}

/**
 * @param {object|null} user
 * @param {number|string} projectId
 * @param {{ revalidate?: boolean }} [opts]
 */
export async function listProjectLibrary(user, projectId, { revalidate = false } = {}) {
  const key = PROJECT_LIBRARY_KEY(user, projectId);
  const { data } = await fetchCached({
    key,
    ttl: TTL.LIBRARY_DOCUMENTS,
    revalidate,
    fetcher: async () => {
      const res = await client.get(`/me/projects/${projectId}/library`);
      return res.data;
    },
  });
  return data;
}

export async function listOrgUnits(projectId) {
  const { data } = await client.get(`/me/projects/${projectId}/org-units`);
  return data;
}

export async function createOrgUnit(projectId, payload) {
  const { data } = await client.post(`/me/projects/${projectId}/org-units`, payload);
  return data;
}

export async function updateOrgUnit(projectId, unitId, payload) {
  const { data } = await client.put(`/me/projects/${projectId}/org-units/${unitId}`, payload);
  return data;
}

export async function deleteOrgUnit(projectId, unitId) {
  await client.delete(`/me/projects/${projectId}/org-units/${unitId}`);
}

export async function bulkUpsertOrgUnits(projectId, entries) {
  const { data } = await client.post(`/me/projects/${projectId}/org-units/bulk`, { entries });
  return data;
}

export async function listCatalogExtensions(projectId) {
  const { data } = await client.get(`/me/projects/${projectId}/catalog-extensions`);
  return data;
}

export async function upsertCatalogExtension(projectId, tcode, payload) {
  const { data } = await client.put(`/me/projects/${projectId}/catalog-extensions/${tcode}`, payload);
  return data;
}

export async function bulkUpsertCatalogExtensions(projectId, entries) {
  const { data } = await client.post(`/me/projects/${projectId}/catalog-extensions`, { entries });
  return data;
}

export async function deleteCatalogExtension(projectId, tcode) {
  await client.delete(`/me/projects/${projectId}/catalog-extensions/${tcode}`);
}
